import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  useQueryGetExamAssignmentAnalysisReport,
  type IAnalysisReportFilters,
} from "@/apis/exam_assignments";
import type {
  IStudentPerformance,
  IModuleAnalysis,
  IStudentModule,
} from "@/apis/exam_assignments";
import { Button } from "@/components/button";
import { FunnelIcon } from "@heroicons/react/16/solid";
import FilteringAnalysisModal, {
  type AnalysisFilterState,
} from "./components/FilteringAnalysisModal";
import ActiveFilters, { type Filter } from "@/components/ActiveFilters";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";
import { useQueryGetUserList } from "@/apis/users/getUserList";

const ExamAssignmentAnalysisPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isDevelopment = false; 

  // Filter state
  const [filters, setFilters] = useState<AnalysisFilterState>({
    filterType: "none",
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Convert filter state to API filters
  const getAPIFilters = (): IAnalysisReportFilters | undefined => {
    switch (filters.filterType) {
      case "exam_time_group":
        return filters.examTimeGroupId
          ? { exam_time_group_id: filters.examTimeGroupId }
          : undefined;
      case "current_group":
        return filters.currentGroupId
          ? { current_group_id: filters.currentGroupId }
          : undefined;
      case "specific_students":
        return filters.selectedUserIds?.length
          ? { user_ids: filters.selectedUserIds.join(",") }
          : undefined;
      default:
        return undefined;
    }
  };

  const {
    data: analysisReport,
    isLoading,
    error,
  } = useQueryGetExamAssignmentAnalysisReport(id || "", getAPIFilters());

  // Get group and user data for filter display
  const { data: groupsData } = useQueryGetGroupList({ limit: 100 });
  const { data: usersData } = useQueryGetUserList({
    page: 1,
    limit: 1000,
    role: "student",
  });

  // Filter handlers
  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (newFilters: AnalysisFilterState) => {
    setFilters(newFilters);
    setIsFilterModalOpen(false);
  };

  const handleRemoveFilter = () => {
    setFilters({ filterType: "none" });
  };

  // Get group name by ID
  const getGroupName = (groupId: string) => {
    const group = groupsData?.items?.find((g) => g.id === groupId);
    return group?.name || "Unknown Group";
  };

  // Get user names by IDs
  const getUserNames = (userIds: string[]) => {
    if (!usersData?.items) return "";
    const users = usersData.items.filter((user) => userIds.includes(user.id));
    if (users.length <= 3) {
      return users.map((u) => u.name).join(", ");
    }
    return `${users
      .slice(0, 3)
      .map((u) => u.name)
      .join(", ")} and ${users.length - 3} more`;
  };

  // Get active filters for display
  const getActiveFilters = (): Filter[] => {
    const activeFilters: Filter[] = [];

    switch (filters.filterType) {
      case "exam_time_group":
        if (filters.examTimeGroupId) {
          activeFilters.push({
            key: "exam_time_group",
            label: `Exam Time Group: ${getGroupName(filters.examTimeGroupId)}`,
            value: filters.examTimeGroupId,
          });
        }
        break;
      case "current_group":
        if (filters.currentGroupId) {
          activeFilters.push({
            key: "current_group",
            label: `Current Group: ${getGroupName(filters.currentGroupId)}`,
            value: filters.currentGroupId,
          });
        }
        break;
      case "specific_students":
        if (filters.selectedUserIds?.length) {
          activeFilters.push({
            key: "specific_students",
            label: `Students: ${getUserNames(filters.selectedUserIds)}`,
            value: filters.selectedUserIds.join(","),
          });
        }
        break;
    }

    return activeFilters;
  };

  // 과목별 모듈 데이터를 정리하는 함수
  const getModulesBySubject = (subject: "reading_writing" | "math") => {
    if (
      !analysisReport ||
      !analysisReport.module_analyses ||
      !analysisReport.student_performances
    )
      return [];

    return analysisReport.module_analyses
      .filter((m: any) => m.subject === subject)
      .map((moduleAnalysis: any) => {
        const studentsForModule = analysisReport.student_performances
          .map((student) => ({
            ...student,
            module: student.modules?.find(
              (m) =>
                m.module_no === moduleAnalysis.module_no &&
                m.subject === subject
            ),
          }))
          .filter((student) => student.module)
          .sort((a, b) => {
            const aScore = a.subject_scores?.[subject] || a.total_score;
            const bScore = b.subject_scores?.[subject] || b.total_score;
            return bScore - aScore;
          });

        return { moduleAnalysis, studentsForModule };
      });
  };

  // Math 모듈이 있는지 확인하는 함수
  const hasMathModules = () => {
    return (
      analysisReport?.module_analyses?.some((m: any) => m.subject === "math") || false
    );
  };

  // Reading & Writing 모듈이 있는지 확인하는 함수
  const hasReadingWritingModules = () => {
    return (
      analysisReport?.module_analyses?.some(
        (m: any) => m.subject === "reading_writing"
      ) || false
    );
  };

  // 문제별 정답률 계산 함수
  const calculateQuestionAccuracy = (
    questionId: string,
    studentsForModule: Array<{
      user: { id: string; name: string; email: string };
      total_score: number;
      module: IStudentModule | undefined;
    }>
  ) => {
    if (!studentsForModule || studentsForModule.length === 0) return 0;

    const totalAnswers = studentsForModule.length;
    const correctAnswers = studentsForModule.filter((student) =>
      student.module?.questions?.find(
        (q) => q.question_id === questionId && q.is_correct
      )
    ).length;

    return totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0;
  };

  // 정답률에 따른 배경색 계산 함수
  const getAccuracyBackgroundColor = (accuracy: number) => {
    if (accuracy <= 50) {
      // 0% ~ 50%: 투명한 빨간색에서 흰색으로
      const intensity = (50 - accuracy) / 50; // 1 (투명한 빨간색) ~ 0 (흰색)
      const red = 255;
      const green = Math.floor(255 - 155 * intensity); // 255에서 100으로
      const blue = Math.floor(255 - 155 * intensity); // 255에서 100으로
      return `rgb(${red}, ${green}, ${blue})`;
    } else {
      // 50% ~ 100%: 흰색에서 짙은 초록색으로
      const intensity = (accuracy - 50) / 50; // 0 (흰색) ~ 1 (짙은 초록색)
      const red = Math.floor(255 - 255 * intensity);
      const green = Math.floor(255 - 85 * intensity); // 255에서 170으로
      const blue = Math.floor(255 - 255 * intensity);
      return `rgb(${red}, ${green}, ${blue})`;
    }
  };

  // 과목별 섹션 컴포넌트
  const SubjectSection = ({
    subject,
  }: {
    subject: "reading_writing" | "math";
  }) => {
    const modulesData = getModulesBySubject(subject);

    if (!modulesData || modulesData.length === 0) return null;

    const sectionTitle =
      subject === "reading_writing" ? "Reading & Writing" : "Math";

    return (
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-4">{sectionTitle}</h3>

        {modulesData.map(({ moduleAnalysis, studentsForModule }, index: number) => (
          <div key={moduleAnalysis.module_no} className="mb-4">
            <div className="overflow-x-auto">
              <table
                className="w-full border-collapse border border-gray-400"
                style={{ fontSize: "10px" }}
              >
                {/* 헤더 */}
                <thead>
                  <tr>
                    <th
                      className="border border-gray-400 bg-gray-200 px-1 py-1 font-medium"
                      rowSpan={3}
                    >
                      Score
                    </th>
                    <th
                      className="border border-gray-400 bg-gray-200 px-1 py-1 font-medium"
                      rowSpan={3}
                    >
                      Module{moduleAnalysis.module_no}
                    </th>
                    <th
                      className="border border-gray-400 bg-gray-200 px-1 py-1 font-medium"
                      rowSpan={3}
                    >
                      Name
                    </th>
                    {moduleAnalysis.questions.map((question: any, index: number) => (
                      <th
                        key={question.question_id}
                        className="border border-gray-400 bg-gray-200 px-[2px] py-1 text-center font-medium"
                      >
                        Q{index + 1}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {moduleAnalysis.questions.map((question: any) => (
                      <th
                        key={question.question_id}
                        className="border border-gray-400 px-[2px] py-1 text-center font-medium"
                        style={{ fontSize: "9px" }}
                      >
                        {question.correct_answer}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {moduleAnalysis.questions.map((question: any) => {
                      const accuracy = calculateQuestionAccuracy(
                        question.question_id,
                        studentsForModule
                      );
                      return (
                        <th
                          key={question.question_id}
                          className="border border-gray-400 py-1 text-center font-medium"
                          style={{
                            fontSize: "9px",
                            backgroundColor:
                              getAccuracyBackgroundColor(accuracy),
                          }}
                        >
                          {accuracy.toFixed(0)}%
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {/* 학생별 답안 */}
                <tbody>
                  {studentsForModule.map((student: any) => (
                    <tr key={student.user.id}>
                      <td className="border border-gray-400 px-[2px] py-[2px] text-center font-medium">
                        {student.subject_scores?.[subject] ||
                          student.total_score}
                      </td>
                      <td className="border border-gray-400 px-[2px] py-[2px] text-center font-medium">
                        {student.module?.correct_answers || 0}/
                        {student.module?.total_questions || 0}
                      </td>
                      <td className="border border-gray-400 px-[2px] py-[2px] whitespace-nowrap text-[11px] text-center">
                        {student.user.name}
                      </td>
                      {moduleAnalysis.questions.map((question: any) => {
                        const studentAnswer = student.module?.questions.find(
                          (q: any) => q.question_id === question.question_id
                        );
                        const isCorrect = studentAnswer?.is_correct || false;
                        const userAnswer = studentAnswer?.user_answer || "";

                        return (
                          <td
                            key={question.question_id}
                            className={`border border-gray-400 px-0 py-[2px] text-center font-medium ${
                              isCorrect ? "text-black" : "bg-red-200 text-black"
                            }`}
                          >
                            {userAnswer}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading analysis report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">
          Error loading analysis report. Please try again.
        </div>
      </div>
    );
  }

  if (!analysisReport) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">No analysis report found.</div>
      </div>
    );
  }

  return (
    <>
      {/* Print CSS to hide filter elements */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* 첫 번째 페이지 - Reading & Writing (Reading & Writing 모듈이 있을 때만) */}
      {hasReadingWritingModules() && (
        <div
          className={`page w-[297mm] h-auto min-h-[210mm] px-6 py-4 text-black font-sans text-base bg-white overflow-hidden ${
            isDevelopment ? "border-2 border-red-500" : ""
          } ${hasMathModules() ? "page-break-after" : ""}`}
          style={{ pageBreakAfter: hasMathModules() ? "always" : "auto" }}
        >
          {/* Header with title and filter button */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-bold">
                {analysisReport.exam_set_title}
              </h1>
              <Button onClick={handleOpenFilterModal} className="no-print">
                <FunnelIcon className="w-4 h-4" />
                Filter
              </Button>
            </div>

            {/* Active Filters */}
            <div className="no-print">
              <ActiveFilters
                filters={getActiveFilters()}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleRemoveFilter}
              />
            </div>
          </div>

          <SubjectSection subject="reading_writing" />
        </div>
      )}

      {/* 두 번째 페이지 - Math (Math 모듈이 있을 때만) */}
      {hasMathModules() && (
        <div
          className={`page w-[297mm] h-auto min-h-[210mm] px-6 py-4 text-black font-sans text-base bg-white overflow-hidden ${
            isDevelopment ? "border-2 border-red-500" : ""
          }`}
        >
          {/* Header with title only */}
          <div className="mb-6">
            <h1 className="text-xl font-bold">
              {analysisReport.exam_set_title}
            </h1>
          </div>

          <SubjectSection subject="math" />
        </div>
      )}

      {/* Filter Modal */}
      <FilteringAnalysisModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        onApply={handleApplyFilters}
        currentFilters={filters}
        examAssignmentId={id}
      />
    </>
  );
};

export default ExamAssignmentAnalysisPage;
